// api.js -- Sprint Hub API Client
// Phase 3B merged: all Phase 1 + Phase 2 + Phase 3 endpoints
// ASCII only. No Unicode.

var API_BASE = '/api';

// X-Ray session ID -- module-level, unique per page load
var _xraySession = (function() {
  try {
    var stored = localStorage.getItem('xray-session');
    if (stored) return stored;
    var id = 'xray-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    localStorage.setItem('xray-session', id);
    return id;
  } catch(e) {
    return 'xray-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
  }
})();

function getXrayHeaders() {
  return { 'x-xray-session': _xraySession };
}

function getImpersonateParam() {
  try { return window.localStorage.getItem('hub_impersonate') || ''; } catch(e) { return ''; }
}

function addImpersonate(url) {
  var imp = getImpersonateParam();
  if (!imp) return url;
  var sep = url.indexOf('?') >= 0 ? '&' : '?';
  return url + sep + 'impersonate=' + encodeURIComponent(imp);
}

function addTenantParam(url) {
  try {
    var tid = localStorage.getItem('hub_tenant');
    if (tid && tid !== 'sprint_mode') {
      var sep = url.indexOf('?') >= 0 ? '&' : '?';
      url = url + sep + 'tenant=' + encodeURIComponent(tid);
    }
  } catch(e) {}
  return url;
}

// Get current tenant from localStorage
export function getCurrentTenant() {
  try { return localStorage.getItem('hub_tenant') || 'sprint_mode'; } catch(e) { return 'sprint_mode'; }
}

// Set current tenant (persists to localStorage, no full reload required)
export function setCurrentTenant(tenantId) {
  try { localStorage.setItem('hub_tenant', tenantId); } catch(e) {}
}

function handleResponse(response) {
  return response.json().then(function(json) {
    if (!json.ok) {
      throw new Error(json.error || 'Request failed');
    }
    return json;
  });
}

function get(path, params) {
  var url = API_BASE + path;
  if (params) {
    var qs = [];
    Object.keys(params).forEach(function(k) {
      if (params[k] != null && params[k] !== '') {
        qs.push(encodeURIComponent(k) + '=' + encodeURIComponent(params[k]));
      }
    });
    if (qs.length > 0) url += '?' + qs.join('&');
  }
  return fetch(addImpersonate(addTenantParam(url)), { headers: getXrayHeaders() }).then(handleResponse);
}

function post(path, body) {
  var h = Object.assign({ 'Content-Type': 'application/json' }, getXrayHeaders());
  return fetch(addImpersonate(addTenantParam(API_BASE + path)), {
    method: 'POST',
    headers: h,
    body: JSON.stringify(body)
  }).then(handleResponse);
}

function patch(path, body) {
  var h = Object.assign({ 'Content-Type': 'application/json' }, getXrayHeaders());
  return fetch(addImpersonate(addTenantParam(API_BASE + path)), {
    method: 'PATCH',
    headers: h,
    body: JSON.stringify(body)
  }).then(handleResponse);
}

function del(path) {
  return fetch(addImpersonate(addTenantParam(API_BASE + path)), { method: 'DELETE', headers: getXrayHeaders() }).then(handleResponse);
}

export function getXraySession() { return _xraySession; }
export function fetchXrayEvents(since) { return get('/xray/events' + (since ? '?since=' + encodeURIComponent(since) : '')); }

// ============================================================
// PHASE 1: Auth & System
// ============================================================

export function getMe() { return get('/me'); }
export function getHealth() { return get('/health'); }
export function getHealthFull() { return get('/health/full'); }
export function getTenants() { return get('/tenants'); }

// ---- Tenant / Hub management (TENANT01v1) ----
export function getMyHubs() { return get('/my-hubs'); }
export function hubSubscribe(body) { return post('/hub/subscribe', body); }
export function hubInvite(body) { return post('/hub/invite', body); }
export function getHubTeam() { return get('/hub/team'); }
export function signupHub(body) {
  return fetch('/api/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  }).then(function(r) { return r.json(); });
}

// ============================================================
// PHASE 1: Brief & Agent
// ============================================================

export function getBrief(params) { return get('/brief', params); }
export function fetchBriefSections(params) { return get('/brief', params); }
export function generateBrief(body) { return post('/brief/generate', body || {}); }
export function askHub(body) { return post('/ask', body); }
export function postAsk(query, history, deepMode) { return post('/ask', { query: query, history: history || [], deep: deepMode }); }
export var askHubV2 = function(query, userEmail) { return post('/hub/ask', { query: query, user_email: userEmail || 'aaron@sprintmode.co' }); };

// ============================================================
// PHASE 1: Tasks
// ============================================================

export function getTasks(params) { return get('/tasks', params); }
export function createTask(body) { return post('/tasks', body); }
export function updateTask(id, body) { return patch('/tasks/' + id, body); }
export function deleteTask(id) { return del('/tasks/' + id); }

// ============================================================
// PHASE 1: Clients
// ============================================================

export function getClients(params) { return get('/clients', params); }
export function getClient(id) { return get('/clients/' + id); }
export function createClient(body) { return post('/clients', body); }
export function updateClient(id, body) { return patch('/clients/' + id, body); }

// ============================================================
// PHASE 1: Investors (Pipeline)
// ============================================================

export function getInvestors(params) { return get('/investors', params); }
export function getInvestor(id) { return get('/investors/' + id); }
export function createInvestor(body) { return post('/investors', body); }
export function updateInvestor(id, body) { return patch('/investors/' + id, body); }
export function getInvestorSummary(params) { return get('/investors/summary', params); }

// ============================================================
// PHASE 1: Deals
// ============================================================

export function getDeals(params) { return get('/deals', params); }
export function getDeal(id) { return get('/deals/' + id); }
export function createDeal(body) { return post('/deals', body); }
export function updateDeal(id, body) { return patch('/deals/' + id, body); }

// ============================================================
// PHASE 1: Initiatives (Projects)
// ============================================================

export function getInitiatives(params) { return get('/initiatives', params); }
export function getInitiative(id) { return get('/initiatives/' + id); }
export function createInitiative(body) { return post('/initiatives', body); }
export function updateInitiative(id, body) { return patch('/initiatives/' + id, body); }

// ============================================================
// PHASE 1: Decisions
// ============================================================

export function getDecisions(params) { return get('/decisions', params); }
export function getMergedDecisions() { return get('/decisions/merged'); }
export function getDecision(id) { return get('/decisions/' + id); }
export function createDecision(body) { return post('/decisions', body); }
export function updateDecision(id, body) { return patch('/decisions/' + id, body); }

// ============================================================
// PHASE 1: Approvals
// ============================================================

export function getApprovals(params) { return get('/approvals', params); }
export function approveApproval(id, body) { return post('/approvals/' + id + '/approve', body); }
export function rejectApproval(id, body) { return post('/approvals/' + id + '/reject', body); }
export function approveDecision(id) { return post('/decisions/' + id + '/approve', {}); }
export function rejectDecision(id, body) { return post('/decisions/' + id + '/reject', body); }

// ============================================================
// PHASE 1: Team
// ============================================================

export function getTeamSignals() { return get('/team/signals'); }

// ============================================================
// PHASE 1: Documents
// ============================================================

export function getDocuments(params) { return get('/documents', params); }
export function updateDocument(id, body) { return patch('/documents/' + id, body); }

// ============================================================
// PHASE 1: Finance
// ============================================================

export function getFinanceSnapshots(params) { return get('/finance/snapshots', params); }
export function getFinanceSummary() { return get('/finance/summary'); }

// ============================================================
// PHASE 1: Governance
// ============================================================

export function getGovernance(params) { return get('/governance', params); }

// ============================================================
// PHASE 1: Events (Audit Log)
// ============================================================

export function getEvents(params) { return get('/events', params); }
export function getHubEvents(params) { return get('/hub/events', params); }

// ============================================================
// PHASE 2: Connectors
// ============================================================

export function getConnectors() { return get('/connectors'); }
export function getPlatformHealth() { return get('/platform/health'); }
export function getPlatformSaas() { return get('/platform/saas'); }
export function syncConnector(provider) { return post('/connectors/' + provider + '/sync'); }
export function getConnectorLog(provider, params) { return get('/connectors/' + provider + '/log', params); }
export function disconnectConnector(provider) { return del('/connectors/' + provider); }

export function connectProvider(provider) { return post('/connectors/connect/' + provider, {}); }
export function getConnectorStatus(provider) { return get('/connectors/status/' + provider); }
export function disconnectProvider(provider) { return post('/connectors/disconnect/' + provider, {}); }
export function triggerConnectorSync(provider) { return get('/connectors/sync/' + provider); }

// ============================================================
// PHASE 2: Emails
// ============================================================

export function getEmails(params) { return get('/emails', params); }
export function getEmail(id) { return get('/emails/' + id); }

// ============================================================
// PHASE 2: Slack
// ============================================================

export function getSlackActivity(params) { return get('/slack/activity', params); }
export function getSlackChannels() { return get('/slack/channels'); }
export function getSlackHighlights(params) { return get('/slack/highlights', params); }

// ============================================================
// PHASE 2: Calendar
// ============================================================

export function getCalendarEvents(params) { return get('/calendar/events', params); }

// ============================================================
// PHASE 2: Finance (extended)
// ============================================================

export function getFinanceTransactions(params) { return get('/finance/transactions', params); }

// ============================================================
// PHASE 2: Notifications
// ============================================================

export function getNotifications(params) { return get('/notifications', params); }
export function markNotificationRead(id) { return patch('/notifications/' + id + '/read'); }
export function markAllNotificationsRead() { return post('/notifications/read-all'); }
export function getNotificationCount() { return get('/notifications/count'); }

// ============================================================
// PHASE 2: Team (extended)
// ============================================================

export function getTeamSignalHistory(params) { return get('/team/signals/history', params); }
export function getTeamRaw(body) { return post('/team/raw', body); }

// ============================================================
// PHASE 2: Investor Activity
// ============================================================

export function getInvestorActivity(id, params) { return get('/investors/' + id + '/activity', params); }

// ============================================================
// PHASE 2: Brief Preferences
// ============================================================

export function getBriefPreferences() { return get('/brief/preferences'); }
export function updateBriefPreferences(body) { return patch('/brief/preferences', body); }

// ============================================================
// PHASE 2: Ask Hub (extended)
// ============================================================

export function getAskConversations(params) { return get('/ask/conversations', params); }
export function getAskConversation(id) { return get('/ask/conversations/' + id); }

// ============================================================
// PHASE 3A: Outbound Email (T21)
// ============================================================

export function getOutboundTemplates(params) { return get('/outbound/templates', params); }
export function createOutboundTemplate(body) { return post('/outbound/templates', body); }
export function updateOutboundTemplate(id, body) { return patch('/outbound/templates/' + id, body); }
export function sendOutboundEmail(body) { return post('/outbound/send', body); }
export function getOutboundHistory(params) { return get('/outbound/history', params); }
export function draftAIEmail(body) { return post('/outbound/draft-ai', body); }
export function getOutboundSequences(params) { return get('/outbound', params); }
export function createOutboundSequence(body) { return post('/outbound', body); }
export function updateOutboundSequence(id, body) { return patch('/outbound/' + id, body); }
export function getSequenceMessages(id) { return get('/outbound/' + id + '/messages'); }

// ============================================================
// PHASE 3A: Agent Actions (T25)
// ============================================================

export function confirmAction(actionId) { return post('/ask/actions/' + actionId + '/confirm'); }
export function rejectAction(actionId) { return post('/ask/actions/' + actionId + '/reject'); }
export function executeAskAction(body) { return post('/ask/execute', body); }

// ============================================================
// PHASE 3A: Pipeline Extensions (T26)
// ============================================================

export function updateInvestorStage(id, body) { return patch('/investors/' + id + '/stage', body); }
export function bulkUpdateInvestors(body) { return post('/investors/bulk-update', body); }

// ============================================================
// PHASE 3A: Global Search (T27)
// ============================================================

export function globalSearch(params) { return get('/search', params); }

// ============================================================
// PHASE 3A: User Preferences (T30)
// ============================================================

export function getUserPreferences() { return get('/me/preferences'); }
export function updateUserPreferences(body) { return patch('/me/preferences', body); }
export function resetUserPreferences() { return post('/me/preferences/reset'); }

// ============================================================
// PHASE 3B: Recruiting (T22)
// ============================================================

export function getCandidates(params) { return get('/recruiting/candidates', params); }
export function createCandidate(body) { return post('/recruiting/candidates', body); }
export function getCandidate(id) { return get('/recruiting/candidates/' + id); }
export function updateCandidate(id, body) { return patch('/recruiting/candidates/' + id, body); }
export function getRecruitingDashboard(params) { return get('/recruiting/dashboard', params); }

// ============================================================
// PHASE 3B: Analytics (T23)
// ============================================================

export function getAnalyticsKPIs(params) { return get('/analytics/kpis', params); }
export function getAnalyticsTrends(params) { return get('/analytics/trends', params); }
export function getAnalyticsEntity(id) { return get('/analytics/entity/' + id); }

// ============================================================
// PHASE 3B: Workflows (T24)
// ============================================================

export function getWorkflows(params) { return get('/workflows', params); }
export function createWorkflow(body) { return post('/workflows', body); }
export function updateWorkflow(id, body) { return patch('/workflows/' + id, body); }
export function deleteWorkflow(id) { return del('/workflows/' + id); }
export function getWorkflowRuns(id, params) { return get('/workflows/' + id + '/runs', params); }
export function testWorkflow(id) { return post('/workflows/' + id + '/test'); }

// ============================================================
// PHASE 3B: LP Reports (T28)
// ============================================================

export function getLPReports(params) { return get('/reports', params); }
export function generateLPReport(body) { return post('/reports/generate', body); }
export function getLPReport(id) { return get('/reports/' + id); }
export function distributeLPReport(id, body) { return post('/reports/' + id + '/distribute', body); }
export function getLPReportTracking(id) { return get('/reports/' + id + '/tracking'); }

// ============================================================
// PHASE 3B: Investor Hub (T28) -- separate worker
// ============================================================

var IH_BASE = '/api/investor-hub';

export function getCapTable() { return get('/investor-hub/cap-table'); }
export function setCapTableVisibility(body) { return post('/investor-hub/cap-table/visibility', body); }
export function getPortalCapTable(investorId) { return get('/investor-hub/cap-table/portal/' + investorId); }

export function getPortfolio() { return get('/investor-hub/portfolio'); }
export function getPortalPortfolio(investorId) { return get('/investor-hub/portfolio/portal/' + investorId); }

export function getIRCalendar(params) { return get('/investor-hub/calendar', params); }
export function createIRCalendarItem(body) { return post('/investor-hub/calendar', body); }
export function updateIRCalendarItem(id, body) { return patch('/investor-hub/calendar/' + id, body); }
export function getPortalCalendar() { return get('/investor-hub/calendar/portal'); }

export function getContent(params) { return get('/investor-hub/content', params); }
export function createContent(body) { return post('/investor-hub/content', body); }
export function approveContent(id) { return post('/investor-hub/content/' + id + '/approve'); }
export function publishContent(id) { return post('/investor-hub/content/' + id + '/publish'); }

export function getCommsThreads(params) { return get('/investor-hub/comms', params); }
export function getCommsMessages(threadId) { return get('/investor-hub/comms/' + threadId + '/messages'); }
export function postCommsMessage(threadId, body) { return post('/investor-hub/comms/' + threadId + '/messages', body); }

export function getAsks() { return get('/investor-hub/asks'); }
export function respondToAsk(askId, body) { return post('/investor-hub/asks/' + askId + '/respond', body); }

export function getPortalActivity(params) { return get('/investor-hub/activity', params); }
export function logPortalActivity(body) { return post('/investor-hub/activity/log', body); }

// ============================================================
// PHASE 4: Jockey
// ============================================================

export function getJockeySessions(params) { return get('/jockey/sessions', params); }
export function getJockeySession(id) { return get('/jockey/sessions/' + id); }
export function getJockeySessionPrompt(id) { return get('/jockey/sessions/' + id + '/prompt'); }
export function getJockeyStats() { return get('/jockey/sessions/stats'); }
export function createJockeySession(data) { return post('/jockey/sessions', data); }
export function batchCreateJockeySessions(sessions) { return post('/jockey/sessions/batch', { sessions: sessions }); }
export function updateJockeySession(id, data) { return patch('/jockey/sessions/' + id, data); }
export function fireJockeySession(id, model) { return post('/jockey/sessions/' + id + '/fire', model ? { model: model } : {}); }
export function approveJockeyDeploy(id) { return post('/jockey/sessions/' + id + '/approve-deploy', {}); }
export function rollbackJockeySession(id) { return post('/jockey/sessions/' + id + '/rollback', {}); }
export function getJockeyDecisions(params) { return get('/jockey/decisions', params); }
export function getBlockingDecisions() { return get('/jockey/decisions/blocking'); }
export function resolveDecision(id, data) { return patch('/jockey/decisions/' + id, data); }
export function getJockeyHealth() { return get('/jockey/health'); }
export function getJockeyEvalLatest() { return get('/jockey/evals/latest'); }
export function getJockeyCloudStatus() { return get('/jockey/cloud-status'); }
export function runJockeyCloudTest() { return post('/jockey/cloud-test', {}); }

// ============================================================
// PHASE 5: Auth (S70) -- Google OAuth session management
// ============================================================

export function getAuthSession() { return get('/auth/session'); }
export function getAuthLoginUrl() { return get('/auth/login'); }
export function authLogout() { return post('/auth/logout', {}); }
export function createInvite(body) { return post('/auth/invite', body); }
export function getInvites() { return get('/auth/invites'); }
export function revokeInvite(id) { return del('/auth/invites/' + id); }

// ============================================================
// PHASE 5: Studios Margin Tracker (S59)
// ============================================================

export var getStudiosEngagements = function(params) { return get('/studios/engagements', params); };
export var getStudiosSummary = function() { return get('/studios/summary'); };
export var updateStudiosEngagement = function(id, data) { return patch('/studios/engagements/' + id, data); };
export var getEntityStatus = function() { return get('/entity-status'); };
export var getFundraseTracking = function() { return get('/fundraise-tracking'); };
export var getExpenseBreakdown = function(params) { return get('/finance/expense-breakdown', params); };
export var getExpenseSummary = function(params) { return get('/finance/expense-breakdown/summary', params); };
export var getFundMetrics = function() { return get('/finance/fund-metrics'); };
export var getFinanceOps = function() { return get('/finance/ops'); };

// Books (operational finance)
export var getBooksPL = function(params) { return get('/books/pl', params); };
export var getBooksBalanceSheet = function() { return get('/books/balance-sheet'); };
export var getBooksTransactions = function(params) { return get('/books/transactions', params); };
export var patchBooksTransaction = function(id, data) { return patch('/books/transactions/' + id, data); };
export var getBooksAR = function() { return get('/books/ar'); };
export var getBooksAP = function() { return get('/books/ap'); };
export var getBooksReport = function(type, params) { return get('/books/reports/' + type, params); };
export var getBooksGeneralLedger = function(params) { return get('/books/general-ledger', params); };

// ============================================================
// PHASE 5.1: Agent Runtime (S75)
// ============================================================

export var listAgents = function(params) { return get('/agents', params); };
export var getAgent = function(id) { return get('/agents/' + id); };
export var updateAgent = function(id, data) { return patch('/agents/' + id, data); };
export var runAgent = function(data) { return post('/agent/run', data); };
export var askAgent = function(id, question, email) { return post('/agents/' + id + '/ask', { question: question, user_email: email }); };
export var getAgentMemory = function(id, params) { return get('/agents/' + id + '/memory', params); };
export var setAgentAutonomy = function(id, data) { return patch('/agents/' + id + '/autonomy', data); };
export var getAgentRuns = function(params) { return get('/agent-runs', params); };
export var submitAgentFeedback = function(data) { return post('/agent/feedback', data); };
export var getAgentActions = function(params) { return get('/agent-actions', params); };
export var approveAgentAction = function(id) { return post('/agent-actions/' + id + '/approve', {}); };
export var rejectAgentAction = function(id) { return post('/agent-actions/' + id + '/reject', {}); };
export var submitFeedback = function(data) { return post('/agent/feedback', data); };
export var executeAction = function(data) { return post('/agent/action', data); };
export var runCfoScenario = function(data) { return post('/agent/cfo/scenario', data); };
export var getCfoScenarios = function() { return get('/agent/cfo/scenarios'); };

// ============================================================
// PHASE 5.1: Bookkeeper Agent (S78)
// ============================================================

export var importTransactions = function(data) { return post('/transactions/import', data); };
export var getTransactions = function(params) { return get('/transactions', params); };
export var updateTransaction = function(id, data) { return patch('/transactions/' + id, data); };
export var getReconciliation = function(params) { return get('/reconciliation', params); };

// ============================================================
// PHASE 5.1: Collections Agent (S79)
// ============================================================

export var createBug = function(data) { return post('/bugs', data); };
export var getCollectionsDashboard = function() { return get('/collections/dashboard'); };
export var postDunning = function(data) { return post('/collections/dunning', data); };
export var postWriteOff = function(data) { return post('/collections/write-off', data); };
export var getCollectionHistory = function(invoiceId) { return get('/collections/history/' + invoiceId); };

// ============================================================
// PHASE 5.1: Triage Agent -- Communication Queue (S82)
// ============================================================

export var triageIngest = function(body) { return post('/triage/ingest', body); };
export var getTriageQueue = function(params) { return get('/triage/queue', params); };
export var triggerTriageProcess = function(body) { return post('/triage/process', body || {}); };

// ============================================================
// PHASE 5.1: Sales Director Agent -- Pipeline (S80)
// ============================================================

export var getPipelineDeals = function(params) { return get('/pipeline', params); };
export var updatePipelineDeal = function(id, data) { return patch('/pipeline/' + id, data); };
export var getPipelineForecast = function() { return get('/pipeline/forecast'); };

// ============================================================
// PHASE 5.1: Customer Success Agent (S87)
// ============================================================

export var getClientHealth = function() { return get('/client-health'); };
export var getClientsHealth = function() { return get('/clients/health'); };

// ============================================================
// S89: Call Agent
// ============================================================

export var createCall = function(data) { return post('/calls', data); };
export var getCalls = function(params) { return get('/calls', params); };
export var getCall = function(id) { return get('/calls/' + id); };
export var submitCallTranscript = function(id, transcript) { return post('/calls/' + id + '/transcript', { transcript: transcript }); };
export var getCallBrief = function(id) { return post('/calls/' + id + '/brief', {}); };
export var processCall = function(id) { return post('/calls/' + id + '/process', {}); };

// N29: Hugh calls/fundraise endpoints
export var getFundraisePipeline = function(params) { return get('/fundraise/pipeline', params); };
export var getFundraiseSummary = function() { return get('/fundraise/summary'); };
export var logFundraseOutcome = function(data) { return post('/fundraise/log-outcome', data); };
export var getCallsUpcoming = function(params) { return get('/calls/upcoming', params); };
export var getCallsHistory = function(params) { return get('/calls/history', params); };
export var prepCall = function(data) { return post('/calls/prep', data); };
export var logCall = function(data) { return post('/calls/log', data); };

// ============================================================
// S95: Client Delivery -- /api/client-delivery
// ============================================================

export var getClientDeliveryConfigs = function() { return get('/client-delivery'); };
export var createClientDeliveryConfig = function(body) { return post('/client-delivery', body); };
export var updateClientDeliveryConfig = function(id, body) { return patch('/client-delivery/' + id, body); };
export var getClientDeliveryProgress = function(id) { return get('/client-delivery/' + id + '/progress'); };

// ============================================================
// S98: Content Library -- /api/content
// ============================================================

export var getContentLibrary = function(params) { return get('/content', params); };
export var createContentPiece = function(body) { return post('/content', body); };
export var updateContentPiece = function(id, body) { return patch('/content/' + id, body); };

// ============================================================
// S99: Pitch Deck Creator
// ============================================================

export var generateDeck = function(body) { return post('/agent/deck/generate', body); };
export var getDeckTemplates = function() { return get('/agent/deck/templates'); };

// ============================================================
// S100: Agent Learning System
// ============================================================

export var getAgentPatterns = function(params) { return get('/agent-patterns', params); };
export var applyAgentPattern = function(id) { return post('/agent-patterns/' + id + '/apply', {}); };
export var getAgentLearningReport = function() { return get('/agent-learning/report'); };

// ============================================================
// H3: Investor Hub Rebuild -- investor_profiles (SM raise)
// ============================================================

export var getInvestorHubSummary = function() { return get('/investor-hub/summary'); };
export var getInvestorRoster = function() { return get('/investor-hub/roster'); };
export var draftInvestorComms = function(body) { return post('/investor-hub/comms', body); };

// ============================================================
// PHASE 4: Labs
// ============================================================

export function getLabsManifest() { return fetch('/api/labs/manifest').then(handleResponse); }
export function updateInitiativeLabs(id, data) { return patch('/initiatives/' + id + '/labs', data); }

// ============================================================
// S120: Pinning -- /api/user/pin
// ============================================================

export var pinItem = function(body) { return post('/user/pin', body); };
export var unpinItem = function(id) { return del('/user/pin/' + id); };

// K1: Brief-specific pinning (anchor cards to Zone 1 indefinitely)
export var pinBriefItem = function(body) { return post('/brief/pin', body); };
export var unpinBriefItem = function(itemId) { return del('/brief/pin/' + itemId); };

// K1: Gmail send (used by AgentActionCard draft flow)
export var sendGmailEmail = function(body) { return post('/connectors/gmail/send', body); };

// ============================================================
// H2: Onboarding wizard (ONBOARD-WIRE-S2)
// ============================================================

export function getOnboardingHubTypes() {
  return fetch('/api/onboarding/hub-types')
    .then(function(r) { return r.json(); })
    .then(function(j) { return j.ok ? j.data : { types: [] }; })
    .catch(function() { return { types: [] }; });
}

export function getOnboardingMatrix(hubType) {
  return fetch('/api/onboarding/matrix?hub_type=' + hubType)
    .then(function(r) { return r.json(); })
    .then(function(j) { return j.ok ? j.data : { agents: [] }; })
    .catch(function() { return { agents: [] }; });
}

export function completeOnboardingWizard(data) {
  return fetch('/api/onboarding/wizard/complete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
    .then(function(r) { return r.json(); })
    .catch(function() { return { ok: false }; });
}

// ============================================================
// S124: Workflow Orchestration Engine -- /api/workflow
// ============================================================

export var triggerWorkflow = function(body) { return post('/workflow/trigger', body); };
export var getWorkflow = function(id) { return get('/workflow/' + id); };
export var approveWorkflowStep = function(id, step) { return post('/workflow/' + id + '/approve', { step: step }); };
export var listWorkflowRuns = function(params) { return get('/workflow', params); };

// ============================================================
// H5: Admin Panel -- /api/admin/*
// ============================================================

function put(path, body) {
  return fetch(addImpersonate(API_BASE + path), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  }).then(handleResponse);
}

// ============================================================
// N142: Hub Sharing config + Hub connector
// ============================================================

export var getSharingConfig = function() { return get('/hub/share/config'); };
export var createSharingGrant = function(body) { return post('/hub/share/config', body); };
export var revokeSharingGrant = function(id) { return del('/hub/share/config/' + id); };

export var getHubConnectors = function() { return get('/connectors/hub'); };
export var addHubConnector = function(body) { return post('/connectors/hub', body); };
export var removeHubConnector = function(id) { return del('/connectors/hub/' + id); };
export var syncHubConnector = function(id) { return post('/connectors/hub/' + id + '/sync', {}); };

export var getAdminConnections = function() { return get('/admin/connections'); };
export var getAdminUsers = function() { return get('/admin/users'); };
export var getAdminUsage = function() { return get('/admin/usage'); };
export var getAdminEvents = function(params) { return get('/admin/events', params); };
export var updateAdminUser = function(email, data) { return put('/admin/users/' + encodeURIComponent(email), data); };

// ============================================================
// MATRIXv1: Agent Network -- /api/network/*
// ============================================================

export var getNetworkFeed = function(params) { return get('/network/feed', params); };
export var getNetworkGraph = function() { return get('/network/graph'); };
export var getNetworkMetrics = function(params) { return get('/network/metrics', params); };
export var logNetworkTransaction = function(body) { return post('/network/transaction', body); };

export var listImportLinks = function() { return get('/import/links'); };
export var createImportLink = function(body) { return post('/import/links', body); };
export var revokeImportLink = function(id) { return del('/import/links/' + id); };

export var getBillingSubscription = function() { return get('/billing/subscription'); };
export var getBillingInvoices = function() { return get('/billing/invoices'); };
export var createBillingCheckout = function(data) { return post('/billing/checkout', data); };
export var createBillingPortal = function() { return post('/billing/portal', {}); };

// -- Default export: compatibility shim for Phase 1/2 files using import api from '../lib/api' --
var api = {
  getMe: getMe,
  getHealth: getHealth,
  getHealthFull: getHealthFull,
  getBrief: getBrief,
  fetchBriefSections: fetchBriefSections,
  generateBrief: generateBrief,
  askHub: askHub,
  askHubV2: askHubV2,
  postAsk: postAsk,
  getTasks: getTasks,
  createTask: createTask,
  updateTask: updateTask,
  deleteTask: deleteTask,
  getClients: getClients,
  getClient: getClient,
  createClient: createClient,
  updateClient: updateClient,
  getInvestors: getInvestors,
  getInvestor: getInvestor,
  createInvestor: createInvestor,
  updateInvestor: updateInvestor,
  getInvestorSummary: getInvestorSummary,
  getInvestorActivity: getInvestorActivity,
  getDeals: getDeals,
  getDeal: getDeal,
  createDeal: createDeal,
  updateDeal: updateDeal,
  getInitiatives: getInitiatives,
  getInitiative: getInitiative,
  createInitiative: createInitiative,
  updateInitiative: updateInitiative,
  getDecisions: getDecisions,
  getDecision: getDecision,
  createDecision: createDecision,
  updateDecision: updateDecision,
  getApprovals: getApprovals,
  approveApproval: approveApproval,
  rejectApproval: rejectApproval,
  getTeamSignals: getTeamSignals,
  getTeamSignalHistory: getTeamSignalHistory,
  postTeamRaw: getTeamRaw,
  getDocuments: getDocuments,
  updateDocument: updateDocument,
  getFinanceSnapshots: getFinanceSnapshots,
  getFinanceSummary: getFinanceSummary,
  getFinanceTransactions: getFinanceTransactions,
  getGovernance: getGovernance,
  getEvents: getEvents,
  getHubEvents: getHubEvents,
  getEmails: getEmails,
  getEmail: getEmail,
  getSlackActivity: getSlackActivity,
  getSlackChannels: getSlackChannels,
  getSlackHighlights: getSlackHighlights,
  getCalendarEvents: getCalendarEvents,
  getConnectors: getConnectors,
  getPlatformHealth: getPlatformHealth,
  getPlatformSaas: getPlatformSaas,
  syncConnector: syncConnector,
  getConnectorLog: getConnectorLog,
  disconnectConnector: disconnectConnector,
  connectProvider: connectProvider,
  getConnectorStatus: getConnectorStatus,
  disconnectProvider: disconnectProvider,
  triggerConnectorSync: triggerConnectorSync,
  getBriefPreferences: getBriefPreferences,
  updateBriefPreferences: updateBriefPreferences,
  getNotifications: getNotifications,
  getNotificationCount: getNotificationCount,
  markNotificationRead: markNotificationRead,
  markAllNotificationsRead: markAllNotificationsRead,
  getAskConversations: getAskConversations,
  getAskConversation: getAskConversation,
  approveDecision: approveDecision,
  rejectDecision: rejectDecision,
  executeAskAction: executeAskAction,
  // Aliases for Phase 1/2 compatibility
  approveItem: approveDecision,
  rejectItem: rejectDecision,
  getFinancialSnapshots: getFinanceSnapshots,
  // Jockey
  getJockeySessions: getJockeySessions,
  getJockeySession: getJockeySession,
  getJockeySessionPrompt: getJockeySessionPrompt,
  getJockeyStats: getJockeyStats,
  createJockeySession: createJockeySession,
  batchCreateJockeySessions: batchCreateJockeySessions,
  updateJockeySession: updateJockeySession,
  fireJockeySession: fireJockeySession,
  approveJockeyDeploy: approveJockeyDeploy,
  rollbackJockeySession: rollbackJockeySession,
  getJockeyDecisions: getJockeyDecisions,
  getBlockingDecisions: getBlockingDecisions,
  resolveDecision: resolveDecision,
  getJockeyHealth: getJockeyHealth,
  getJockeyEvalLatest: getJockeyEvalLatest,
  getJockeyCloudStatus: getJockeyCloudStatus,
  runJockeyCloudTest: runJockeyCloudTest,
  // Labs
  getLabsManifest: getLabsManifest,
  updateInitiativeLabs: updateInitiativeLabs,
  // Pinning (S120)
  pinItem: pinItem,
  unpinItem: unpinItem,
  // Studios
  getStudiosEngagements: getStudiosEngagements,
  getStudiosSummary: getStudiosSummary,
  updateStudiosEngagement: updateStudiosEngagement,
  getEntityStatus: getEntityStatus,
  getFundraseTracking: getFundraseTracking,
  getExpenseBreakdown: getExpenseBreakdown,
  getExpenseSummary: getExpenseSummary,
  getFundMetrics: getFundMetrics,
  // H3: Investor Hub
  getInvestorHubSummary: getInvestorHubSummary,
  getInvestorRoster: getInvestorRoster,
  draftInvestorComms: draftInvestorComms,
  // Auth (S70)
  getAuthSession: getAuthSession,
  getAuthLoginUrl: getAuthLoginUrl,
  authLogout: authLogout,
  // Invites (S71)
  createInvite: createInvite,
  getInvites: getInvites,
  revokeInvite: revokeInvite,
  // Agent runtime (S75)
  listAgents: listAgents,
  getAgent: getAgent,
  updateAgent: updateAgent,
  runAgent: runAgent,
  askAgent: askAgent,
  getAgentMemory: getAgentMemory,
  setAgentAutonomy: setAgentAutonomy,
  getAgentRuns: getAgentRuns,
  submitAgentFeedback: submitAgentFeedback,
  getAgentActions: getAgentActions,
  approveAgentAction: approveAgentAction,
  rejectAgentAction: rejectAgentAction,
  // Collections (S79)
  getCollectionsDashboard: getCollectionsDashboard,
  postDunning: postDunning,
  postWriteOff: postWriteOff,
  getCollectionHistory: getCollectionHistory,
  // Triage agent (S82)
  triageIngest: triageIngest,
  getTriageQueue: getTriageQueue,
  triggerTriageProcess: triggerTriageProcess,
  // Sales Director pipeline (S80)
  getPipelineDeals: getPipelineDeals,
  updatePipelineDeal: updatePipelineDeal,
  getPipelineForecast: getPipelineForecast,
  // Client delivery (S95)
  getClientDeliveryConfigs: getClientDeliveryConfigs,
  createClientDeliveryConfig: createClientDeliveryConfig,
  updateClientDeliveryConfig: updateClientDeliveryConfig,
  getClientDeliveryProgress: getClientDeliveryProgress,
  getClientsHealth: getClientsHealth,
  // Pitch Deck Creator (S99)
  generateDeck: generateDeck,
  getDeckTemplates: getDeckTemplates,
  // Generic fetch for direct URL calls
  get: function(url) { return fetch(url).then(handleResponse); },
};

export default api;
